import { useEffect, useState, useMemo, useRef } from 'react';
import InputMask from 'react-input-mask';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { makeStyles } from '@material-ui/core/styles';
import MUITextField from '@material-ui/core/TextField';
import moment from 'moment';
import classnames from 'classnames';
import hexRgba from 'hex-rgba';
import { FixedSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { color } from '../../app/theme';

const useStyles = makeStyles(
  theme => ({
    root: {
      ...theme.typography.subtitle1,
      position: 'relative',
    },
    Modal: {
      position: 'absolute',
      marginTop: 4,
      display: 'flex',
      width: 92,
      height: 216,
      padding: 8,
      marginLeft: 4,
      borderColor: color.$4,
      borderRadius: 4,
      backgroundColor: color.$7,
      boxShadow:
        '0px 16px 48px 0px rgba(0, 0, 0, 0.07), 0px 6px 18px 0px rgba(0, 0, 0, 0.03)',
      zIndex: 2,
    },
    Timepicker: {
      display: 'flex',
      borderRadius: '22px',
      padding: '11px 16px 10px 24px',
      ...theme.input.default.wrapper,
      color: color.$13,
      backgroundColor: `${color.$7}`,
      '& input': {
        height: '21px',
        fontSize: '15px',
        padding: 0,
        '&::placeholder': {
          color: color.$10,
          opacity: 1,
        },
      },
      '& .MuiInputBase-root': {
        '&:before, &:after': {
          display: 'none',
        },
      },
    },
    isFocus: {
      border: `1px solid  ${color.$1}`,
    },
    options: {
      overflowY: 'scroll',
      overflowX: 'hidden',
      '&::-webkit-scrollbar': {
        width: 0,
      },
      '&>div>div': {
        fontSize: 15,
        fontWeight: 400,
        color: color.$13,
        borderRadius: 4,
        padding: '4px 12px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: 0,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: hexRgba(color.$1, 40),
        },
      },
    },
    isSelect: {
      backgroundColor: `${color.$1} !important`,
      color: `${color.$7} !important`,
    },
  }),
  {
    name: 'TimePicker',
  },
);

const TimePicker = ({
  selectedTime,
  className,
  placeholder,
  onChange,
  disabled,
  isUp,
}) => {
  const FORMAT_TYPE = 'HH : mm';

  const [isFocus, setIsFocus] = useState(false);
  const classes = useStyles();

  const [timeString, setTimeString] = useState(
    moment(selectedTime).format(FORMAT_TYPE),
  );

  const [hours, setHours] = useState([]);
  const [minutes, setMinutes] = useState([]);

  const hourRef = useRef();
  const minuteRef = useRef();

  const isHoursItemLoaded = index => index < hours.length;
  const isMinutesItemLoaded = index => index < minutes.length;

  const getHours = useMemo(() => {
    const values = [];
    for (let i = 0; i < 24; i += 1) {
      values.push(i.toString().padStart(2, '0'));
    }
    return values;
  });

  const getMinutes = useMemo(() => {
    const values = [];
    for (let i = 0; i < 60; i += 1) {
      values.push(i.toString().padStart(2, '0'));
    }
    return values;
  });

  const findHourIndex = useMemo(() => {
    return hours.findIndex(d => d === moment(selectedTime).format('HH'));
  }, [hours, selectedTime]);

  const findMinuteIndex = useMemo(() => {
    return minutes.findIndex(d => d === moment(selectedTime).format('mm'));
  }, [minutes, selectedTime]);

  const onTextFieldChange = e => {
    const t = e.target.value;
    const date = moment(t, FORMAT_TYPE);

    setTimeString(t);

    if (onChange && date.isValid() && /\d{2} : \d{2}/.test(t)) {
      onChange(date);

      const findNowHourIndex = hours.findIndex(
        d => d === moment(date).format('HH'),
      );

      const findNowMinuteIndex = minutes.findIndex(
        d => d === moment(date).format('mm'),
      );

      hourRef.current.scrollToItem(findNowHourIndex, 'start');
      minuteRef.current.scrollToItem(findNowMinuteIndex, 'start');
      setTimeout(() => {
        hourRef.current.scrollToItem(findNowHourIndex, 'start');
        minuteRef.current.scrollToItem(findNowMinuteIndex, 'start');
      });
    }
  };

  const onSetHour = d => {
    const newTime = moment(selectedTime).hour(d);

    setTimeString(newTime.format(FORMAT_TYPE));
    onChange(newTime);
  };

  const onSetMinute = d => {
    const newTime = moment(selectedTime).minute(d);

    setTimeString(newTime.format(FORMAT_TYPE));
    onChange(newTime);
  };

  const onClickAway = () => {
    setIsFocus(false);
  };

  useEffect(() => {
    setTimeString(moment(selectedTime).format(FORMAT_TYPE));
  }, [selectedTime]);

  useEffect(() => {
    setHours(getHours);
    setMinutes(getMinutes);

    return () => {
      setHours([]);
      setMinutes([]);
    };
  }, [isFocus]);

  const onScrollHour = () => {
    setHours([...hours, ...getHours]);
  };

  const onScrollMinute = () => {
    setMinutes([...minutes, ...getMinutes]);
  };

  const onFocus = () => {
    setIsFocus(true);

    setTimeout(() => {
      hourRef.current.scrollToItem(findHourIndex, 'start');
      minuteRef.current.scrollToItem(findMinuteIndex, 'start');

      setTimeout(() => {
        hourRef.current.scrollToItem(findHourIndex, 'start');
        minuteRef.current.scrollToItem(findMinuteIndex, 'start');
      });
    });
  };

  const hourRow = ({ index, style }) => {
    const data = hours[index];
    return (
      <div
        className={classnames({
          [classes.isSelect]: data === hours[findHourIndex],
        })}
        onClick={() => onSetHour(data)}
        style={style}
      >
        {data}
      </div>
    );
  };

  const minuteRow = ({ index, style }) => {
    const data = minutes[index];
    return (
      <div
        className={classnames({
          [classes.isSelect]: data === minutes[findMinuteIndex],
        })}
        onClick={() => onSetMinute(data)}
        style={style}
      >
        {data}
      </div>
    );
  };

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <div className={classnames([classes.root, className])}>
        <InputMask
          mask="99 : 99"
          autoComplete="off"
          maskChar={null}
          value={timeString}
          disabled={disabled}
          onChange={onTextFieldChange}
          onFocus={onFocus}
        >
          {inputProps => (
            <MUITextField
              className={classnames(classes.Timepicker, {
                [classes.isFocus]: isFocus,
              })}
              type="text"
              disabled={disabled}
              placeholder={placeholder}
              {...inputProps}
            />
          )}
        </InputMask>
        {isFocus && (
          <div
            className={classes.Modal}
            style={isUp ? { top: -226 } : undefined}
          >
            <InfiniteLoader
              isItemLoaded={isHoursItemLoaded}
              itemCount={hours.length + 1}
              loadMoreItems={onScrollHour}
              threshold={1}
            >
              {({ onItemsRendered, ref }) => (
                <FixedSizeList
                  className={classes.options}
                  ref={list => {
                    ref(list);
                    hourRef.current = list;
                  }}
                  height={200}
                  onItemsRendered={onItemsRendered}
                  itemCount={hours.length + 1}
                  itemSize={29}
                  width={42}
                >
                  {hourRow}
                </FixedSizeList>
              )}
            </InfiniteLoader>

            <InfiniteLoader
              isItemLoaded={isMinutesItemLoaded}
              itemCount={minutes.length + 1}
              loadMoreItems={onScrollMinute}
              threshold={1}
            >
              {({ onItemsRendered, ref }) => (
                <FixedSizeList
                  className={classes.options}
                  style={{ marginLeft: 4 }}
                  ref={list => {
                    ref(list);
                    minuteRef.current = list;
                  }}
                  height={200}
                  onItemsRendered={onItemsRendered}
                  itemCount={minutes.length + 1}
                  itemSize={29}
                  width={42}
                >
                  {minuteRow}
                </FixedSizeList>
              )}
            </InfiniteLoader>
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
};

export default TimePicker;
